import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../utils/apiClient";
import { getApiBaseUrl } from "../utils/apiBaseUrl";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Rating,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";

const API_BASE_URL = getApiBaseUrl();

const steps = ["Fast Start", "Profile Details", "Consent"];
const BASELINE_MAX_SECONDS = 60;

const commonOptions = {
  primaryGoal: ["Mock interviews", "Placement prep", "Job switch", "Leadership prep", "Consulting readiness"],
  industries: ["Software", "Finance", "Consulting", "Product", "Healthcare", "Operations", "Sales"],
  interviewTimeline: ["Within 2 weeks", "Within 1 month", "1-3 months", "3+ months"],
  prepIntensity: ["Light", "Moderate", "Intense"],
  learningStyle: ["Rapid drills", "Detailed review", "Balanced"],
  targetRoles: ["SDE", "Data", "Product", "Analyst", "Consultant", "Design", "Management"],
};

const studentOptions = {
  educationLevel: ["Undergraduate", "Postgraduate", "Bootcamp", "Other"],
  graduationTimeline: ["Current semester", "Within 6 months", "Within 12 months", "Already graduated"],
  majorDomain: ["Computer Science", "Electronics", "Business", "Design", "Other"],
  placementReadiness: ["Just starting", "Practicing regularly", "Interview ready"],
};

const professionalOptions = {
  seniorityLevel: ["Foundation", "Growth", "Advanced", "Leadership"],
  managementScope: ["Individual contributor", "Mentoring", "Team lead", "Org lead"],
  targetCompanyType: ["Startup", "Mid-size", "Enterprise", "Global MNC"],
  careerTransitionIntent: ["Same role growth", "Role switch", "Industry switch", "Leadership track"],
  noticePeriodBand: ["Immediate", "Within 30 days", "31-60 days", "60+ days"],
};

const countryOptions = [
  { code: "IN", label: "India" },
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
  { code: "CA", label: "Canada" },
  { code: "AU", label: "Australia" },
  { code: "SG", label: "Singapore" },
  { code: "AE", label: "United Arab Emirates" },
  { code: "OTHER", label: "Other" },
];

const timezoneOptions = [
  "Asia/Kolkata",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Helsinki",
  "Asia/Singapore",
  "Asia/Dubai",
  "Australia/Sydney",
];

const interviewFormats = ["Phone screen", "Technical", "System design", "Behavioral", "Case", "Take-home"];

const skillOptions = [
  { key: "python_fundamentals", label: "Python fundamentals" },
  { key: "java_full_stack", label: "Java full stack" },
  { key: "machine_learning_python", label: "Machine learning with Python" },
  { key: "genai_python_cloud", label: "GenAI, Python, and cloud" },
  { key: "frontend_engineering", label: "Frontend engineering" },
  { key: "data_analytics_sql_python", label: "Data analytics with SQL and Python" },
  { key: "devops_cloud_basics", label: "DevOps and cloud basics" },
  { key: "testing_qa_automation", label: "Testing and QA automation" },
  { key: "cybersecurity_basics", label: "Cybersecurity basics" },
  { key: "product_analyst_basics", label: "Product analyst basics" },
];

const initialForm = {
  userCategory: "professional",
  primaryGoal: "",
  targetRoles: [],
  industries: [],
  interviewTimeline: "",
  prepIntensity: "Moderate",
  learningStyle: "Balanced",
  consentDataUse: false,
  consentContact: false,
  stateCode: "",
  city: "",
  countryCode: "IN",
  region: "",
  timezone: "Asia/Kolkata",
  fullNameOverride: "",
  universityName: "",
  degreeName: "",
  graduationYear: "",
  primaryStream: "",
  skillRatings: {},
  educationLevel: "",
  graduationTimeline: "",
  majorDomain: "",
  placementReadiness: "",
  currentRole: "",
  yearsOfExperience: "",
  seniorityLevel: "",
  managementScope: "",
  domainExpertise: [],
  targetCompanyType: "",
  careerTransitionIntent: "",
  noticePeriodBand: "",
  interviewUrgency: "",
  interviewFormat: "",
  targetJobDescription: "",
  targetJobUrl: "",
  linkedinUrl: "",
  githubUrl: "",
  portfolioUrl: "",
  resumeText: "",
  resumeDraft: null,
  baselineCapture: null,
};

const extrasStorageKey = "onboardingProfileExtras";
const canonicalProfileFields = [
  "candidateType", "fullNameOverride", "stateCode", "city", "countryCode", "region", "timezone",
  "universityName", "universityNormalized", "degreeLevel", "degreeName", "branchSpecialization",
  "graduationYear", "currentYearOfStudy", "experienceLevel", "primaryStream", "targetRoles",
  "targetCompanies", "skillsSelfReported", "seniority", "yearsOfExperience", "currentTitle",
  "targetInterviewFormat", "targetJobDescription", "targetJobUrl", "resumeUrl", "linkedinUrl",
  "githubUrl", "portfolioUrl", "resumeText", "resumeDraft", "baselineCapture", "consentDataUse",
  "consentContact",
];

function readExtras() {
  try {
    return JSON.parse(localStorage.getItem(extrasStorageKey) || "{}");
  } catch (_err) {
    return {};
  }
}

function writeExtras(form) {
  localStorage.setItem(
    extrasStorageKey,
    JSON.stringify({
      timezone: form.timezone,
      region: form.region,
      yearsOfExperience: form.yearsOfExperience,
      seniorityLevel: form.seniorityLevel,
      interviewFormat: form.interviewFormat,
      skillRatings: form.skillRatings,
      targetJobDescription: form.targetJobDescription,
      targetJobUrl: form.targetJobUrl,
      fullNameOverride: form.fullNameOverride,
      linkedinUrl: form.linkedinUrl,
      githubUrl: form.githubUrl,
      portfolioUrl: form.portfolioUrl,
      resumeText: form.resumeText,
      resumeDraft: form.resumeDraft,
      baselineCapture: form.baselineCapture,
    }),
  );
}

function normalizeTargetRoles(values) {
  if (!Array.isArray(values)) return [];
  return [...new Set(values
    .map((value) => (typeof value === "string" ? value.trim().slice(0, 120) : ""))
    .filter(Boolean))].slice(0, 10);
}

function targetCompaniesForSave(profile, selectedCompany) {
  const existing = normalizeTargetRoles(profile?.targetCompanies);
  const selected = typeof selectedCompany === "string" ? selectedCompany.trim() : "";
  return normalizeTargetRoles(selected ? [selected, ...existing] : existing);
}

function numberOrNull(value) {
  return value === "" || value == null ? null : Number(value);
}

function toCareerCompBand(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["foundation", "growth", "advanced", "leadership"].includes(normalized)) {
    return normalized[0].toUpperCase() + normalized.slice(1);
  }
  if (["student", "intern", "early_career", "junior"].includes(normalized)) return "Foundation";
  if (normalized === "mid") return "Growth";
  if (["senior", "lead"].includes(normalized)) return "Leadership";
  return "";
}

function toExperienceLevel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["student", "intern"].includes(normalized)) return "student";
  if (["early_career", "junior", "foundation"].includes(normalized)) return "early_career";
  if (["mid", "growth"].includes(normalized)) return "mid";
  if (["senior", "lead", "advanced", "leadership"].includes(normalized)) return "senior";
  return "";
}

function buildCanonicalEditorPayload(profile, form, profileSkills) {
  const payload = {};
  canonicalProfileFields.forEach((key) => {
    if (profile?.[key] !== undefined) payload[key] = profile[key];
  });
  return {
    ...payload,
    candidateType: form.userCategory === "student" ? "student" : "professional",
    fullNameOverride: form.fullNameOverride || null,
    stateCode: form.stateCode || null,
    city: form.city || null,
    countryCode: form.countryCode || "IN",
    region: form.region || profile?.region || null,
    timezone: form.timezone || null,
    universityName: form.universityName || null,
    degreeLevel: form.educationLevel || null,
    degreeName: form.degreeName || null,
    branchSpecialization: form.majorDomain || null,
    graduationYear: numberOrNull(form.graduationYear),
    currentYearOfStudy: form.graduationTimeline || null,
    experienceLevel: toExperienceLevel(form.seniorityLevel) || null,
    primaryStream: form.primaryStream || null,
    targetRoles: normalizeTargetRoles(form.targetRoles),
    targetCompanies: targetCompaniesForSave(profile, form.targetCompanyType),
    skillsSelfReported: profileSkills,
    seniority: toExperienceLevel(form.seniorityLevel) || null,
    yearsOfExperience: numberOrNull(form.yearsOfExperience),
    currentTitle: form.currentRole || null,
    targetInterviewFormat: form.interviewFormat || null,
    targetJobDescription: form.targetJobDescription || null,
    targetJobUrl: form.targetJobUrl || null,
    linkedinUrl: form.linkedinUrl || null,
    githubUrl: form.githubUrl || null,
    portfolioUrl: form.portfolioUrl || null,
    resumeText: form.resumeText || null,
    resumeDraft: form.resumeDraft || null,
    baselineCapture: form.baselineCapture || null,
    consentDataUse: form.consentDataUse,
    consentContact: form.consentContact,
  };
}

function createBaselineCapture(status, fields = {}) {
  const now = new Date().toISOString();
  const capture = {
    status,
    audio: false,
    video: false,
    consentGranted: false,
    ...fields,
  };
  if (status === "captured") {
    capture.capturedAt = fields.capturedAt || now;
  }
  if (status === "skipped") {
    capture.skippedAt = fields.skippedAt || now;
  }
  if (status === "failed") {
    capture.failedAt = fields.failedAt || now;
  }
  return capture;
}

function formatDraftFieldValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === "object") {
          return item.label || item.name || item.key || JSON.stringify(item);
        }
        return String(item);
      })
      .join(", ");
  }
  return String(value);
}

function SelectChips({ title, options, value, onChange, helperText }) {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {options.map((option) => (
          <Chip
            key={option}
            label={option}
            clickable
            color={value === option ? "primary" : "default"}
            variant={value === option ? "filled" : "outlined"}
            onClick={() => onChange(option)}
            sx={{ borderRadius: "10px" }}
          />
        ))}
      </Stack>
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
    </Stack>
  );
}

function MultiSelectChips({ title, options, value, onChange }) {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {options.map((option) => {
          const selected = value.includes(option);
          return (
            <Chip
              key={option}
              label={option}
              clickable
              color={selected ? "primary" : "default"}
              variant={selected ? "filled" : "outlined"}
              onClick={() => onChange(option)}
              sx={{ borderRadius: "10px" }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

function ProfileTypeButton({ selected, title, description, onClick }) {
  return (
    <Button
      fullWidth
      variant={selected ? "contained" : "outlined"}
      onClick={onClick}
      sx={{
        alignItems: "flex-start",
        borderRadius: 2,
        justifyContent: "flex-start",
        minHeight: 112,
        p: 2,
        textAlign: "left",
        textTransform: "none",
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: selected ? "primary.contrastText" : "text.secondary" }}>
          {description}
        </Typography>
      </Stack>
    </Button>
  );
}

function SkillRatingGrid({ ratings, onChange }) {
  return (
    <Stack spacing={1.25}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Skill self-ratings
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Rate skills from 1 to 5. The strongest rated skill becomes the primary stream for interview calibration.
        </Typography>
      </Box>
      <Grid container spacing={1.5}>
        {skillOptions.map((skill) => (
          <Grid item xs={12} sm={6} md={4} key={skill.key}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: ratings[skill.key] ? "primary.main" : "divider",
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Typography id={`${skill.key}-label`} variant="body2" sx={{ fontWeight: 700 }}>
                {skill.label}
              </Typography>
              <Rating
                aria-labelledby={`${skill.key}-label`}
                name={`rating-${skill.key}`}
                value={ratings[skill.key] || 0}
                onChange={(_event, nextValue) => onChange(skill.key, nextValue || 0)}
                max={5}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { getToken, isLoaded, isSignedIn } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [resumeParsing, setResumeParsing] = useState(false);
  const [resumeDraft, setResumeDraft] = useState(null);
  const [resumeGap, setResumeGap] = useState(null);
  const [baselineStatus, setBaselineStatus] = useState("idle");
  const [baselineRecorder, setBaselineRecorder] = useState(null);
  const [canonicalProfile, setCanonicalProfile] = useState(null);
  const [legacyProfilePresent, setLegacyProfilePresent] = useState(true);
  const [skillsEdited, setSkillsEdited] = useState(false);
  const [form, setForm] = useState(() => ({ ...initialForm, ...readExtras() }));

  const setBaselineCapture = (capture, status = capture.status) => {
    setForm((prev) => ({ ...prev, baselineCapture: capture }));
    setBaselineStatus(status);
  };

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setLoading(false);
      return;
    }
    let mounted = true;
    async function loadProfile() {
      try {
        const token = await getToken();
        const resp = await authFetch(`${API_BASE_URL}/api/profile/me`, token, { method: "GET" });
        if (!mounted) return;
        if (resp.status === 404) {
          setLoading(false);
          return;
        }
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(text || "Failed to load profile");
        }
        const body = await resp.json();
        const responseProfile = body?.profile || body;
        const loadedCanonicalProfile = body?.candidateProfileV2 || responseProfile?.candidateProfileV2 || null;
        const profile = {
          ...responseProfile,
          ...(loadedCanonicalProfile || {}),
        };
        setCanonicalProfile(loadedCanonicalProfile);
        setLegacyProfilePresent(body?.legacyProfilePresent !== false);
        const extras = readExtras();
        const parsedYears = parseInt(profile.experienceBand, 10);
        const userCategory = loadedCanonicalProfile?.candidateType
          ? loadedCanonicalProfile.candidateType === "student" ? "student" : "professional"
          : profile.userCategory ||
            (profile.candidateType === "student" || profile.candidateType === "early_career" ? "student" : "professional");
        setForm((prev) => ({
          ...prev,
          ...extras,
          userCategory,
          primaryGoal: profile.primaryGoal || "",
          targetRoles: normalizeTargetRoles(profile.targetRoles),
          industries: Array.isArray(profile.industries) ? profile.industries : [],
          interviewTimeline: profile.interviewTimeline || "",
          prepIntensity: profile.prepIntensity || "Moderate",
          learningStyle: profile.learningStyle || "Balanced",
          consentDataUse: Boolean(profile.consentDataUse),
          consentContact: Boolean(profile.consentContact),
          stateCode: profile.stateCode || "",
          region: profile.region || extras.region || "",
          city: profile.city || "",
          countryCode: profile.countryCode || "IN",
          timezone: profile.timezone || extras.timezone || "Asia/Kolkata",
          fullNameOverride: profile.fullNameOverride || profile.candidateProfileV2?.fullNameOverride || extras.fullNameOverride || "",
          universityName: profile.universityName || "",
          degreeName: profile.degreeName || "",
          graduationYear: profile.graduationYear ? String(profile.graduationYear) : "",
          primaryStream: profile.primaryStream || "",
          educationLevel: profile.educationLevel || profile.degreeLevel || "",
          graduationTimeline: profile.graduationTimeline || profile.currentYearOfStudy || "",
          majorDomain: profile.majorDomain || profile.branchSpecialization || "",
          placementReadiness: profile.placementReadiness || "",
          currentRole: profile.currentRole || profile.currentTitle || "",
          managementScope: profile.managementScope || "",
          domainExpertise: Array.isArray(profile.domainExpertise) ? profile.domainExpertise : [],
          targetCompanyType: profile.targetCompanyType || profile.targetCompanies?.[0] || "",
          careerTransitionIntent: profile.careerTransitionIntent || "",
          noticePeriodBand: profile.noticePeriodBand || "",
          interviewUrgency: profile.interviewUrgency || "",
          seniorityLevel: toCareerCompBand(
            profile.seniority || profile.careerCompBand || extras.seniorityLevel,
          ),
          yearsOfExperience: profile.yearsOfExperience != null ? String(profile.yearsOfExperience) : (extras.yearsOfExperience || (Number.isFinite(parsedYears) ? String(parsedYears) : "")),
          interviewFormat: profile.targetInterviewFormat || extras.interviewFormat || "",
          targetJobDescription: profile.targetJobDescription || profile.candidateProfileV2?.targetJobDescription || extras.targetJobDescription || "",
          targetJobUrl: profile.targetJobUrl || profile.candidateProfileV2?.targetJobUrl || extras.targetJobUrl || "",
          linkedinUrl: profile.linkedinUrl || profile.candidateProfileV2?.linkedinUrl || extras.linkedinUrl || "",
          githubUrl: profile.githubUrl || profile.candidateProfileV2?.githubUrl || extras.githubUrl || "",
          portfolioUrl: profile.portfolioUrl || profile.candidateProfileV2?.portfolioUrl || extras.portfolioUrl || "",
          resumeText: profile.resumeText || profile.candidateProfileV2?.resumeText || extras.resumeText || "",
          resumeDraft: profile.resumeDraft || profile.candidateProfileV2?.resumeDraft || extras.resumeDraft || null,
          baselineCapture: profile.baselineCapture || profile.candidateProfileV2?.baselineCapture || extras.baselineCapture || null,
          skillRatings: Array.isArray(profile.skillsSelfReported)
            ? profile.skillsSelfReported.reduce((acc, item) => {
                if (item && typeof item === "object" && item.key && item.rating) acc[item.key] = item.rating;
                return acc;
              }, extras.skillRatings || {})
            : extras.skillRatings || {},
        }));
      } catch (loadErr) {
        setError(loadErr.message || "Failed to load profile.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }
    loadProfile();
    return () => {
      mounted = false;
    };
  }, [isLoaded, isSignedIn, getToken]);

  const selectedSkills = useMemo(
    () =>
      Object.entries(form.skillRatings || {})
        .filter(([, rating]) => rating > 0)
        .sort((a, b) => b[1] - a[1]),
    [form.skillRatings],
  );

  const categoryLabel = form.userCategory === "student" ? "Student" : "Working Professional";
  const targetRoleOptions = useMemo(
    () => normalizeTargetRoles([...commonOptions.targetRoles, ...form.targetRoles]),
    [form.targetRoles],
  );

  const toggleMulti = (field, option) => {
    setForm((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      return {
        ...prev,
        [field]: current.includes(option)
          ? current.filter((item) => item !== option)
          : [...current, option],
      };
    });
  };

  const setSkillRating = (skillKey, rating) => {
    setSkillsEdited(true);
    setForm((prev) => {
      const nextRatings = { ...(prev.skillRatings || {}), [skillKey]: rating };
      if (!rating) {
        delete nextRatings[skillKey];
      }
      const topSkill = Object.entries(nextRatings).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
      return {
        ...prev,
        skillRatings: nextRatings,
        primaryStream: topSkill,
      };
    });
  };

  const applyResumeDraft = (draft) => {
    const fields = draft?.fields || {};
    const fieldValue = (name) => fields[name]?.value;
    const parsedSkills = Array.isArray(fieldValue("skillsSelfReported")) ? fieldValue("skillsSelfReported") : [];
    const parsedRatings = parsedSkills.reduce((acc, skill) => {
      if (skill?.key && skill.rating) acc[skill.key] = skill.rating;
      return acc;
    }, {});
    setForm((prev) => ({
      ...prev,
      universityName: fieldValue("universityName") || prev.universityName,
      fullNameOverride: fieldValue("fullNameOverride") || prev.fullNameOverride,
      degreeName: fieldValue("degreeName") || prev.degreeName,
      graduationYear: fieldValue("graduationYear") ? String(fieldValue("graduationYear")) : prev.graduationYear,
      educationLevel: fieldValue("educationLevel") || prev.educationLevel,
      currentRole: fieldValue("currentTitle") || prev.currentRole,
      yearsOfExperience: fieldValue("yearsOfExperience") != null ? String(fieldValue("yearsOfExperience")) : prev.yearsOfExperience,
      industries: Array.isArray(fieldValue("industries")) && fieldValue("industries").length ? fieldValue("industries") : prev.industries,
      linkedinUrl: fieldValue("linkedinUrl") || prev.linkedinUrl,
      githubUrl: fieldValue("githubUrl") || prev.githubUrl,
      portfolioUrl: fieldValue("portfolioUrl") || prev.portfolioUrl,
      skillRatings: Object.keys(parsedRatings).length ? { ...prev.skillRatings, ...parsedRatings } : prev.skillRatings,
      primaryStream: Object.keys(parsedRatings)[0] || prev.primaryStream,
      resumeText: draft?.rawText || prev.resumeText,
      resumeDraft: draft || prev.resumeDraft,
    }));
  };

  const parseResume = async ({ file } = {}) => {
    setResumeParsing(true);
    setError("");
    try {
      const token = await getToken();
      let contentBase64 = null;
      if (file) {
        contentBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      const resp = await authFetch(`${API_BASE_URL}/api/profile/resume-draft`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileName: file?.name || null,
          contentBase64,
          resumeText: file ? null : form.resumeText,
          targetJobDescription: form.targetJobDescription || null,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || "Failed to parse resume.");
      }
      const data = await resp.json();
      setResumeDraft(data.draft);
      setResumeGap(data.gapAnalysis || null);
    } catch (parseErr) {
      setError(parseErr.message || "Failed to parse resume.");
    } finally {
      setResumeParsing(false);
    }
  };

  const startBaselineCapture = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setBaselineCapture(
        createBaselineCapture("failed", {
          reason: "unsupported_browser",
        }),
        "unsupported",
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      const recorder = new MediaRecorder(stream);
      const startedAt = new Date();
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const durationSeconds = Math.max(1, Math.round((Date.now() - startedAt.getTime()) / 1000));
        const baselineCapture = {
          status: "captured",
          capturedAt: startedAt.toISOString(),
          durationSeconds: Math.min(durationSeconds, BASELINE_MAX_SECONDS),
          audio: true,
          video: true,
          consentGranted: true,
          source: "onboarding_browser_media",
        };
        setBaselineCapture(baselineCapture, "captured");
        setBaselineRecorder(null);
      };
      recorder.start();
      setBaselineRecorder(recorder);
      setBaselineStatus("recording");
      window.setTimeout(() => {
        if (recorder.state === "recording") recorder.stop();
      }, BASELINE_MAX_SECONDS * 1000);
    } catch (err) {
      setBaselineCapture(
        createBaselineCapture("failed", {
          reason: err?.name === "NotAllowedError" ? "permission_denied" : "capture_start_failed",
        }),
        "error",
      );
    }
  };

  const stopBaselineCapture = () => {
    if (baselineRecorder?.state === "recording") {
      baselineRecorder.stop();
    }
  };

  const skipBaselineCapture = () => {
    if (baselineRecorder?.state === "recording") {
      baselineRecorder.stop();
    }
    setBaselineRecorder(null);
    setBaselineCapture(
      createBaselineCapture("skipped", {
        reason: "user_skipped",
      }),
      "skipped",
    );
  };

  const validateStep = (step) => {
    if (step === 0) {
      return Boolean(
        (normalizeTargetRoles(form.targetRoles).length > 0 || form.targetJobDescription.trim()) &&
          form.seniorityLevel
      );
    }
    if (step === 1) {
      return true;
    }
    if (step === 2) {
      return Boolean(form.consentDataUse);
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      setError("Please complete the required fields before continuing.");
      return;
    }
    setError("");
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setError("");
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) {
      setError("Data-use consent is required to continue. Contact consent is optional.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const token = await getToken();
      const selectedSkillRatings = selectedSkills.map(([key, rating]) => {
        const skill = skillOptions.find((item) => item.key === key);
        return {
          key,
          label: skill?.label || key,
          rating,
        };
      });
      const profileSkills = skillsEdited
        ? selectedSkillRatings
        : (Array.isArray(canonicalProfile?.skillsSelfReported)
          ? canonicalProfile.skillsSelfReported
          : selectedSkillRatings);
      const domainExpertise = skillsEdited
        ? selectedSkillRatings.map((skill) => `${skill.label} (${skill.rating}/5)`)
        : form.domainExpertise;
      const payload = {
        userCategory: form.userCategory,
        primaryGoal: form.primaryGoal,
        targetRoles: normalizeTargetRoles(form.targetRoles),
        industries: form.industries,
        interviewTimeline: form.interviewTimeline,
        prepIntensity: form.prepIntensity,
        learningStyle: form.learningStyle,
        consentDataUse: form.consentDataUse,
        consentContact: form.consentContact,
        stateCode: form.stateCode || null,
        region: form.region || canonicalProfile?.region || null,
        city: form.city || null,
        countryCode: form.countryCode || "IN",
        timezone: form.timezone || null,
        fullNameOverride: form.fullNameOverride || null,
        universityName: form.universityName || null,
        degreeName: form.degreeName || null,
        graduationYear: numberOrNull(form.graduationYear),
        primaryStream: form.primaryStream || null,
        skillsSelfReported: profileSkills,
        seniority: toExperienceLevel(form.seniorityLevel) || null,
        experienceLevel: toExperienceLevel(form.seniorityLevel) || null,
        yearsOfExperience: numberOrNull(form.yearsOfExperience),
        currentTitle: form.currentRole || null,
        targetInterviewFormat: form.interviewFormat || null,
        targetJobDescription: form.targetJobDescription || null,
        targetJobUrl: form.targetJobUrl || null,
        linkedinUrl: form.linkedinUrl || null,
        githubUrl: form.githubUrl || null,
        portfolioUrl: form.portfolioUrl || null,
        resumeText: form.resumeText || null,
        resumeDraft: form.resumeDraft || null,
        baselineCapture: form.baselineCapture || null,
        educationLevel: form.educationLevel || null,
        graduationTimeline: form.graduationTimeline || null,
        majorDomain: form.majorDomain || null,
        placementReadiness: form.placementReadiness || null,
        currentRole: form.currentRole || null,
        experienceBand: form.yearsOfExperience === "" || form.yearsOfExperience == null ? null : `${form.yearsOfExperience} years`,
        managementScope: form.managementScope || null,
        domainExpertise,
        targetCompanyType: form.targetCompanyType || null,
        careerTransitionIntent: form.careerTransitionIntent || null,
        noticePeriodBand: form.noticePeriodBand || null,
        careerCompBand: form.seniorityLevel || null,
        interviewUrgency: form.interviewUrgency || null,
      };
      const canonicalPayload = canonicalProfile
        ? buildCanonicalEditorPayload(canonicalProfile, form, profileSkills)
        : null;
      if (legacyProfilePresent || !canonicalPayload) {
        const legacyResponse = await authFetch(`${API_BASE_URL}/api/profile/me`, token, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!legacyResponse.ok) {
          const text = await legacyResponse.text();
          throw new Error(text || "Failed to save onboarding profile.");
        }
      } else {
        const canonicalResponse = await authFetch(`${API_BASE_URL}/api/profile`, token, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(canonicalPayload),
        });
        if (!canonicalResponse.ok) {
          const text = await canonicalResponse.text();
          throw new Error(text || "Failed to preserve canonical profile details.");
        }
      }
      writeExtras(form);
      navigate("/dashboard");
    } catch (submitErr) {
      setError(submitErr.message || "Failed to save onboarding profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "70vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 5 },
        bgcolor: "#f6f8fb",
        backgroundImage: "linear-gradient(180deg, #eef3f8 0%, #f8fafc 42%, #ffffff 100%)",
      }}
    >
      <Container maxWidth="lg">
        <Card
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: "rgba(15,23,42,0.1)",
            boxShadow: "0 24px 70px rgba(15,23,42,0.12)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              bgcolor: "#101820",
              color: "common.white",
              p: { xs: 2.5, md: 4 },
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={7}>
                <Stack spacing={1.25}>
                  <Chip
                    label="Onboarding calibration"
                    size="small"
                    sx={{
                      alignSelf: "flex-start",
                      bgcolor: "rgba(255,255,255,0.1)",
                      color: "common.white",
                      border: "1px solid rgba(255,255,255,0.2)",
                    }}
                  />
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 850, letterSpacing: 0 }}>
                    Build your interview intelligence profile
                  </Typography>
                  <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.72)", maxWidth: 680 }}>
                    A compact setup that turns role, skill, consent, and optional voice/video baseline signals into better interview calibration.
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={5}>
                <Grid container spacing={1.25}>
                  {[
                    ["01", "Goal"],
                    ["02", "Signals"],
                    ["03", "Consent"],
                  ].map(([index, label]) => (
                    <Grid item xs={4} key={label}>
                      <Box
                        sx={{
                          border: "1px solid rgba(255,255,255,0.16)",
                          borderRadius: 1.5,
                          p: 1.25,
                          bgcolor: activeStep + 1 === Number(index) ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.06)",
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.58)" }}>
                          {index}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          {label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack spacing={3}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {error && <Alert severity="error">{error}</Alert>}

              {activeStep === 0 && (
                <Stack spacing={2.5}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <ProfileTypeButton
                        selected={form.userCategory === "student"}
                        title="Student"
                        description="Campus placements, internships, and first-job interview prep."
                        onClick={() => setForm((prev) => ({ ...prev, userCategory: "student" }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ProfileTypeButton
                        selected={form.userCategory === "professional"}
                        title="Working Professional"
                        description="Growth, transitions, and high-stakes interview coaching."
                        onClick={() => setForm((prev) => ({ ...prev, userCategory: "professional" }))}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Profile Type: {categoryLabel}
                  </Typography>

                  <SelectChips
                    title="Primary interview goal"
                    options={commonOptions.primaryGoal}
                    value={form.primaryGoal}
                    onChange={(option) => setForm((prev) => ({ ...prev, primaryGoal: option }))}
                  />
                  <MultiSelectChips
                    title="Target role"
                    options={targetRoleOptions}
                    value={form.targetRoles}
                    onChange={(option) => toggleMulti("targetRoles", option)}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <SelectChips
                        title="Seniority"
                        options={professionalOptions.seniorityLevel}
                        value={form.seniorityLevel}
                        onChange={(option) => setForm((prev) => ({ ...prev, seniorityLevel: option }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectChips
                        title="Target interview format"
                        options={interviewFormats}
                        value={form.interviewFormat}
                        onChange={(option) => setForm((prev) => ({ ...prev, interviewFormat: option }))}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              )}

              {activeStep === 1 && (
                <Stack spacing={2.5}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth required>
                        <InputLabel id="country-label">Country</InputLabel>
                        <Select
                          labelId="country-label"
                          label="Country"
                          value={form.countryCode}
                          onChange={(e) => setForm((prev) => ({ ...prev, countryCode: e.target.value }))}
                        >
                          {countryOptions.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                              {country.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        required
                        label="Region / State"
                        value={form.stateCode}
                        onChange={(e) => setForm((prev) => ({ ...prev, stateCode: e.target.value }))}
                        helperText="Use a state, province, emirate, or region name."
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth required>
                        <InputLabel id="timezone-label">Timezone</InputLabel>
                        <Select
                          labelId="timezone-label"
                          label="Timezone"
                          value={form.timezone}
                          onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
                        >
                          {timezoneOptions.map((timezone) => (
                            <MenuItem key={timezone} value={timezone}>
                              {timezone}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>Used for reminders and interview scheduling context.</FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <SkillRatingGrid ratings={form.skillRatings} onChange={setSkillRating} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Profile name"
                        value={form.fullNameOverride}
                        onChange={(e) => setForm((prev) => ({ ...prev, fullNameOverride: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Current title"
                        value={form.currentRole}
                        onChange={(e) => setForm((prev) => ({ ...prev, currentRole: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Years of experience"
                        inputProps={{ min: 0, max: 60, step: 1 }}
                        value={form.yearsOfExperience}
                        onChange={(e) => setForm((prev) => ({ ...prev, yearsOfExperience: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="City"
                        value={form.city}
                        onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="University / College"
                        value={form.universityName}
                        onChange={(e) => setForm((prev) => ({ ...prev, universityName: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Degree Name"
                        value={form.degreeName}
                        onChange={(e) => setForm((prev) => ({ ...prev, degreeName: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Graduation Year"
                        value={form.graduationYear}
                        onChange={(e) => setForm((prev) => ({ ...prev, graduationYear: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="Target job description"
                        value={form.targetJobDescription}
                        onChange={(e) => setForm((prev) => ({ ...prev, targetJobDescription: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={2}>
                        <TextField
                          fullWidth
                          label="Job URL"
                          value={form.targetJobUrl}
                          onChange={(e) => setForm((prev) => ({ ...prev, targetJobUrl: e.target.value }))}
                        />
                        <TextField
                          fullWidth
                          label="LinkedIn URL"
                          value={form.linkedinUrl}
                          onChange={(e) => setForm((prev) => ({ ...prev, linkedinUrl: e.target.value }))}
                        />
                        <TextField
                          fullWidth
                          label="GitHub URL"
                          value={form.githubUrl}
                          onChange={(e) => setForm((prev) => ({ ...prev, githubUrl: e.target.value }))}
                        />
                        <TextField
                          fullWidth
                          label="Portfolio URL"
                          value={form.portfolioUrl}
                          onChange={(e) => setForm((prev) => ({ ...prev, portfolioUrl: e.target.value }))}
                        />
                      </Stack>
                    </Grid>
                  </Grid>

                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Resume import
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Parsed fields are reviewed here before they are saved to your profile.
                          </Typography>
                        </Box>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                          <Button variant="outlined" component="label" disabled={resumeParsing}>
                            Upload PDF/DOCX
                            <input
                              hidden
                              type="file"
                              accept=".pdf,.docx"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) parseResume({ file });
                              }}
                            />
                          </Button>
                          <Button
                            variant="outlined"
                            disabled={resumeParsing || !form.resumeText.trim()}
                            onClick={() => parseResume()}
                          >
                            {resumeParsing ? "Parsing..." : "Parse pasted resume"}
                          </Button>
                        </Stack>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          label="Paste resume text"
                          value={form.resumeText}
                          onChange={(e) => setForm((prev) => ({ ...prev, resumeText: e.target.value }))}
                        />
                        {resumeDraft && (
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1.5}>
                              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Parsed profile draft
                                </Typography>
                                <Button size="small" variant="contained" onClick={() => applyResumeDraft(resumeDraft)}>
                                  Apply Draft
                                </Button>
                              </Stack>
                              <Grid container spacing={1}>
                                {Object.entries(resumeDraft.fields || {})
                                  .filter(([, meta]) => {
                                    if (Array.isArray(meta?.value)) return meta.value.length > 0;
                                    return meta?.value;
                                  })
                                  .map(([name, meta]) => (
                                    <Grid item xs={12} md={6} key={name}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{formatDraftFieldValue(meta.value)}</Typography>
                                        <Chip size="small" label={`${Math.round((meta.confidence || 0) * 100)}%`} />
                                      </Stack>
                                    </Grid>
                                  ))}
                              </Grid>
                            </Stack>
                          </Paper>
                        )}
                        {resumeGap && (
                          <Alert severity={resumeGap.gaps?.length ? "warning" : "success"}>
                            Readiness {resumeGap.readinessScore}%. Focus: {(resumeGap.suggestedFocusAreas || []).join(", ") || "No major gaps detected."}
                          </Alert>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>

                  {form.userCategory === "student" ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <SelectChips
                          title="Education level"
                          options={studentOptions.educationLevel}
                          value={form.educationLevel}
                          onChange={(option) => setForm((prev) => ({ ...prev, educationLevel: option }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SelectChips
                          title="Placement readiness"
                          options={studentOptions.placementReadiness}
                          value={form.placementReadiness}
                          onChange={(option) => setForm((prev) => ({ ...prev, placementReadiness: option }))}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <SelectChips
                          title="Management scope"
                          options={professionalOptions.managementScope}
                          value={form.managementScope}
                          onChange={(option) => setForm((prev) => ({ ...prev, managementScope: option }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SelectChips
                          title="Target company type"
                          options={professionalOptions.targetCompanyType}
                          value={form.targetCompanyType}
                          onChange={(option) => setForm((prev) => ({ ...prev, targetCompanyType: option }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SelectChips
                          title="Transition intent"
                          options={professionalOptions.careerTransitionIntent}
                          value={form.careerTransitionIntent}
                          onChange={(option) => setForm((prev) => ({ ...prev, careerTransitionIntent: option }))}
                        />
                      </Grid>
                    </Grid>
                  )}

                  <MultiSelectChips
                    title="Preferred industries"
                    options={commonOptions.industries}
                    value={form.industries}
                    onChange={(option) => toggleMulti("industries", option)}
                  />
                </Stack>
              )}

              {activeStep === 2 && (
                <Stack spacing={2}>
                  <Alert severity="info">
                    Data-use consent is required for personalized coaching. Contact consent is optional.
                  </Alert>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.consentDataUse}
                        onChange={(e) => setForm((prev) => ({ ...prev, consentDataUse: e.target.checked }))}
                      />
                    }
                    label="I consent to profile and interview data usage for personalized coaching, analytics, and service improvement."
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.consentContact}
                        onChange={(e) => setForm((prev) => ({ ...prev, consentContact: e.target.checked }))}
                      />
                    }
                    label="Optional: I agree to be contacted for interview support, updates, and follow-up guidance."
                  />
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(16,24,32,0.02)",
                      borderColor: form.baselineCapture ? "primary.main" : "divider",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Optional 60-second voice/video baseline
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Uses browser-native camera and microphone access. Only consented status metadata is saved here.
                          </Typography>
                        </Box>
                        {form.baselineCapture?.status && (
                          <Chip
                            size="small"
                            color={
                              form.baselineCapture.status === "captured"
                                ? "success"
                                : form.baselineCapture.status === "failed"
                                  ? "warning"
                                  : "default"
                            }
                            label={`Baseline ${form.baselineCapture.status}`}
                            sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                          />
                        )}
                      </Stack>
                      {form.baselineCapture?.status === "captured" && (
                        <Alert severity="success">
                          Baseline captured for {form.baselineCapture.durationSeconds}s.
                        </Alert>
                      )}
                      {form.baselineCapture?.status === "skipped" && (
                        <Alert severity="info">Baseline skipped. Setup can still be completed.</Alert>
                      )}
                      {baselineStatus === "recording" && (
                        <Alert severity="info">Recording baseline. Stop when you finish, or it will stop at 60 seconds.</Alert>
                      )}
                      {baselineStatus === "unsupported" && (
                        <Alert severity="warning">Baseline capture is not supported in this browser. A failed fallback status will be saved.</Alert>
                      )}
                      {baselineStatus === "error" && (
                        <Alert severity="warning">Baseline capture could not start. A failed fallback status will be saved.</Alert>
                      )}
                      <Stack direction="row" spacing={1}>
                        {baselineStatus === "recording" ? (
                          <Button variant="contained" color="secondary" onClick={stopBaselineCapture}>
                            Stop Recording
                          </Button>
                        ) : (
                          <Button variant="outlined" onClick={startBaselineCapture}>
                            Start Baseline
                          </Button>
                        )}
                        <Button
                          onClick={skipBaselineCapture}
                          disabled={baselineStatus === "recording"}
                        >
                          Skip
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </Stack>
              )}

              <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                <Button disabled={activeStep === 0 || saving} onClick={handleBack}>
                  Back
                </Button>
                {activeStep < steps.length - 1 ? (
                  <Button variant="contained" onClick={handleNext} disabled={saving}>
                    Next
                  </Button>
                ) : (
                  <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                    {saving ? "Saving..." : "Complete Setup"}
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
